import { useEffect, useState, type FormEvent } from 'react'
import { errorMessage, get, patch, post } from '../../api'
import { Alert, Button, Field, Input, Modal, Segmented, Select, StatusChip, Textarea } from '../../components/ui'
import { useLoad } from '../../hooks'
import { addDays, longDate, pluralGuests, shortDate, todayKey } from '../../format'
import type { AvailabilitySlot, Booking, BookingStatus } from '../../types'
import { useBusiness } from './Layout'

// The two dialogs staff use most: taking a booking, and looking at or changing one.

const TRANSITIONS: Partial<Record<BookingStatus, { event: string; label: string; variant?: 'primary' | 'secondary' | 'danger' }[]>> = {
  confirmed: [
    { event: 'seated', label: 'Seat', variant: 'primary' },
    { event: 'no_show', label: 'No-show', variant: 'danger' },
  ],
  awaiting_approval: [
    { event: 'approved', label: 'Approve', variant: 'primary' },
    { event: 'declined', label: 'Decline', variant: 'danger' },
  ],
  seated: [{ event: 'finished', label: 'Finish', variant: 'primary' }],
}

export function quickActions(status: BookingStatus) {
  return TRANSITIONS[status] ?? []
}

function SlotPicker({ date, party, areaId, value, onChange, excludeBookingId }: { date: string; party: number; areaId?: string; value: string; onChange: (t: string) => void; excludeBookingId?: string }) {
  const { business } = useBusiness()
  const q = new URLSearchParams({ date, party: String(party) })
  if (areaId) q.set('area', areaId)
  const slots = useLoad(() => get<{ closed: boolean; reason: string | null; slots: AvailabilitySlot[] }>(`/v1/admin/businesses/${business.id}/availability?${q}`), [business.id, date, party, areaId])
  if (slots.loading && !slots.data) return <p className="small muted">Checking availability…</p>
  if (slots.error) return <Alert>{slots.error}</Alert>
  if (!slots.data || slots.data.closed) return <p className="note-box">Closed on {shortDate(date)}{slots.data?.reason ? ` (${slots.data.reason})` : ''}.</p>
  const open = slots.data.slots.filter((s) => s.status !== 'unavailable')
  return (
    <div className="stack">
      <div className="slot-grid">
        {slots.data.slots.map((s) => (
          <button key={s.time} type="button" className={`${s.status === 'limited' ? 'limited' : ''} ${value === s.time ? 'selected' : ''}`} disabled={s.status === 'unavailable' && value !== s.time} onClick={() => onChange(s.time)} aria-pressed={value === s.time}>
            {s.time}
          </button>
        ))}
      </div>
      {open.length === 0 ? <p className="note-box">Fully booked for {pluralGuests(party)} on this day{excludeBookingId ? ' (other than this booking)' : ''}. Staff can still overbook by typing a time below.</p> : <p className="hint">Dashed times are nearly full.</p>}
    </div>
  )
}

export function NewBookingModal({ initialDate, onClose, onCreated }: { initialDate: string; onClose: () => void; onCreated: (b: Booking) => void }) {
  const { business } = useBusiness()
  const [source, setSource] = useState<'phone' | 'walk_in' | 'staff'>('phone')
  const [date, setDate] = useState(initialDate)
  const [time, setTime] = useState('')
  const [party, setParty] = useState(2)
  const [areaId, setAreaId] = useState('')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [requests, setRequests] = useState('')
  const [notes, setNotes] = useState('')
  const [sendConfirmation, setSendConfirmation] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (source === 'walk_in') {
      setDate(todayKey(business.timezone))
      const now = new Date()
      const parts = new Intl.DateTimeFormat('en-GB', { timeZone: business.timezone, hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(now)
      const h = Number(parts.find((p) => p.type === 'hour')?.value ?? 0)
      const m = Number(parts.find((p) => p.type === 'minute')?.value ?? 0)
      const rounded = Math.floor(m / 15) * 15
      setTime(`${String(h).padStart(2, '0')}:${String(rounded).padStart(2, '0')}`)
    }
  }, [source, business.timezone])

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!time) {
      setError('Pick a time.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const created = await post<Booking>(`/v1/admin/businesses/${business.id}/bookings`, {
        date,
        time,
        partySize: party,
        areaId: areaId || undefined,
        guest: { name: name.trim(), email: email.trim() || undefined, phone: phone.trim() || undefined },
        source,
        specialRequests: requests.trim() || undefined,
        internalNotes: notes.trim() || undefined,
        sendConfirmation: sendConfirmation && !!email.trim(),
      })
      onCreated(created)
    } catch (err) {
      setError(errorMessage(err))
      setBusy(false)
    }
  }

  return (
    <Modal
      title="New booking"
      onClose={onClose}
      wide
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="new-booking" busy={busy}>
            {source === 'walk_in' ? 'Seat now' : 'Book'}
          </Button>
        </>
      }
    >
      <form id="new-booking" onSubmit={submit} className="stack">
        {error ? <Alert>{error}</Alert> : null}
        <Segmented
          label="How the booking came in"
          value={source}
          onChange={setSource}
          options={[
            { value: 'phone', label: 'Phone' },
            { value: 'walk_in', label: 'Walk-in' },
            { value: 'staff', label: 'Other' },
          ]}
        />
        <div className="inline-fields">
          <Field label="Date">{(id) => <Input id={id} type="date" value={date} min={addDays(todayKey(business.timezone), -1)} onChange={(e) => setDate(e.target.value)} required />}</Field>
          <Field label="Guests">{(id) => <Input id={id} type="number" min={1} max={200} value={party} onChange={(e) => setParty(Math.max(1, Number(e.target.value)))} required />}</Field>
          {business.areas.length > 1 ? (
            <Field label="Area">
              {(id) => (
                <Select id={id} value={areaId} onChange={(e) => setAreaId(e.target.value)}>
                  <option value="">Any</option>
                  {business.areas.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          ) : null}
        </div>
        {source !== 'walk_in' ? (
          <SlotPicker date={date} party={party} areaId={areaId || undefined} value={time} onChange={setTime} />
        ) : null}
        <Field label={source === 'walk_in' ? 'Seated at' : 'Time'}>{(id) => <Input id={id} type="time" value={time} onChange={(e) => setTime(e.target.value)} step={900} required />}</Field>
        <Field label="Guest name">{(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} required autoComplete="off" />}</Field>
        <div className="grid-2">
          <Field label="Phone">{(id) => <Input id={id} value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" autoComplete="off" />}</Field>
          <Field label="Email" hint={email && sendConfirmation ? 'A confirmation with the manage link will be sent.' : undefined}>
            {(id) => <Input id={id} type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" />}
          </Field>
        </div>
        {email ? (
          <label className="row-flex small" style={{ cursor: 'pointer' }}>
            <input type="checkbox" checked={sendConfirmation} onChange={(e) => setSendConfirmation(e.target.checked)} /> Email the guest a confirmation
          </label>
        ) : null}
        <Field label="Guest requests (visible to the guest)">{(id) => <Textarea id={id} value={requests} onChange={(e) => setRequests(e.target.value)} rows={2} />}</Field>
        <Field label="Internal notes (staff only)">{(id) => <Textarea id={id} value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />}</Field>
      </form>
    </Modal>
  )
}

export function BookingDetailModal({ booking, onClose, onChange }: { booking: Booking; onClose: () => void; onChange: (b: Booking) => void }) {
  const { business } = useBusiness()
  const [tab, setTab] = useState<'details' | 'move' | 'cancel'>('details')
  const [notes, setNotes] = useState(booking.internalNotes ?? '')
  const [date, setDate] = useState(booking.date)
  const [time, setTime] = useState(booking.time)
  const [party, setParty] = useState(booking.partySize)
  const [areaId, setAreaId] = useState(booking.areaId ?? '')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const run = async (fn: () => Promise<Booking>) => {
    setBusy(true)
    setError(null)
    try {
      onChange(await fn())
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }
  const base = `/v1/admin/businesses/${business.id}/bookings/${booking.id}`
  const transition = (event: string, r?: string) => run(() => post<Booking>(`${base}/transition`, { event, reason: r || undefined }))
  const saveNotes = () => run(() => patch<Booking>(base, { internalNotes: notes.trim() || null, version: booking.version }))
  const move = () => run(() => patch<Booking>(base, { date, time, partySize: party, areaId: areaId || null, version: booking.version })).then(() => setTab('details'))
  const canMove = booking.status === 'confirmed' || booking.status === 'awaiting_approval'
  const canCancel = canMove || booking.status === 'seated'
  const actions = quickActions(booking.status)

  return (
    <Modal title={`${booking.guest.name} · ${pluralGuests(booking.partySize)}`} onClose={onClose} wide>
      <div className="stack-lg">
        <div className="spread">
          <div>
            <div style={{ fontWeight: 650, fontSize: 16 }}>
              {longDate(booking.date)} at {booking.time}
            </div>
            <div className="muted small">
              {booking.areaName ?? 'Any area'} · ref {booking.reference} · {booking.source.replace('_', '-')}
            </div>
          </div>
          <StatusChip status={booking.status} />
        </div>
        {error ? <Alert>{error}</Alert> : null}
        {actions.length ? (
          <div className="quick-actions">
            {actions.map((a) => (
              <Button key={a.event} variant={a.variant ?? 'secondary'} busy={busy} onClick={() => void transition(a.event)}>
                {a.label}
              </Button>
            ))}
          </div>
        ) : null}
        <div className="tabs" role="tablist">
          <a role="tab" href="#details" className={tab === 'details' ? 'active' : ''} onClick={(e) => (e.preventDefault(), setTab('details'))}>
            Details
          </a>
          {canMove ? (
            <a role="tab" href="#move" className={tab === 'move' ? 'active' : ''} onClick={(e) => (e.preventDefault(), setTab('move'))}>
              Move
            </a>
          ) : null}
          {canCancel ? (
            <a role="tab" href="#cancel" className={tab === 'cancel' ? 'active' : ''} onClick={(e) => (e.preventDefault(), setTab('cancel'))}>
              Cancel booking
            </a>
          ) : null}
        </div>

        {tab === 'details' ? (
          <div className="stack">
            <dl className="detail-grid">
              <dt>Phone</dt>
              <dd>{booking.guest.phone ? <a href={`tel:${booking.guest.phone}`}>{booking.guest.phone}</a> : '—'}</dd>
              <dt>Email</dt>
              <dd>{booking.guest.email ? <a href={`mailto:${booking.guest.email}`}>{booking.guest.email}</a> : '—'}</dd>
              <dt>Occasion</dt>
              <dd>{booking.occasion ?? '—'}</dd>
              <dt>Requests</dt>
              <dd>{booking.specialRequests ?? '—'}</dd>
              <dt>Booked</dt>
              <dd>{new Date(booking.createdAt).toLocaleString()}</dd>
            </dl>
            <Field label="Internal notes">{(id) => <Textarea id={id} value={notes} onChange={(e) => setNotes(e.target.value)} />}</Field>
            {notes !== (booking.internalNotes ?? '') ? (
              <div className="row-flex">
                <Button variant="primary" busy={busy} onClick={() => void saveNotes()}>
                  Save notes
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}

        {tab === 'move' ? (
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault()
              void move()
            }}
          >
            <div className="inline-fields">
              <Field label="Date">{(id) => <Input id={id} type="date" value={date} onChange={(e) => setDate(e.target.value)} required />}</Field>
              <Field label="Guests">{(id) => <Input id={id} type="number" min={1} max={200} value={party} onChange={(e) => setParty(Math.max(1, Number(e.target.value)))} />}</Field>
              {business.areas.length > 1 ? (
                <Field label="Area">
                  {(id) => (
                    <Select id={id} value={areaId} onChange={(e) => setAreaId(e.target.value)}>
                      <option value="">Any</option>
                      {business.areas.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
              ) : null}
            </div>
            <SlotPicker date={date} party={party} areaId={areaId || undefined} value={time} onChange={setTime} excludeBookingId={booking.id} />
            <Field label="Time">{(id) => <Input id={id} type="time" value={time} onChange={(e) => setTime(e.target.value)} step={900} required />}</Field>
            <p className="hint">{booking.guest.email ? 'The guest will get an email with the new time.' : 'The guest has no email on file, so let them know by phone.'}</p>
            <div className="row-flex">
              <Button type="submit" variant="primary" busy={busy}>
                Move booking
              </Button>
            </div>
          </form>
        ) : null}

        {tab === 'cancel' ? (
          <div className="stack">
            <p className="small">The guest {booking.guest.email ? 'will be emailed that the booking is cancelled' : 'has no email on file, so let them know by phone'}. The table goes back on sale immediately.</p>
            <Field label="Reason (internal)">{(id) => <Input id={id} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Guest called to cancel" />}</Field>
            <div className="row-flex">
              <Button variant="danger" busy={busy} onClick={() => void transition('cancelled_by_venue', reason)}>
                Cancel this booking
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </Modal>
  )
}
